<template>
  <v-container class="invoice-tab">
    <v-row justify="end" align-content="end">
      <v-col cols="4" class="text-right">
        <v-btn
            color="primary"
            elevation="0"
            outlined
            :disabled="!isThereInvoices"
            @click="() => {$refs.invoiceSignDialog.openDialog()}"
        >{{$t('book/issue:common.sign-invoice')}}</v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <DataTablePaginated
            ref="table"
            class="ma-1"
            :headers="headers"
            icon="edit"
            :iconTooltip="$t('buttons:edit')"
            :addBtnText="$t('buttons:add-specific', {specific: $t('book/issue:invoiceDocumentation')})"
            @clickedAddButton="createInvoice"
            @clickedButton1="v => $refs.invoiceDocumentationDialog.openDialog(v)"
            icon2="delete"
            :iconTooltip2="$t('buttons:delete')"
            icon3="visibility"
            :iconTooltip3="$t('buttons:view')"
            @clickedButton2="openDeleteDialog"
            @clickedButton3="v => $refs.invoiceDocumentationDialog.viewDialog(v)"
            @updateTableData="updateTable"
            :dependsOnProperty3="'isSigned'"
            :dependsOnProperty2="'editable'"
            :dependsOnProperty="'editable'"
            :addBtn="!taskInfo.finish"
            sort-by="id"
            :sortDesc="true"
            :decorateFields="[{name: 'status', method: this.decorateStatus}]"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <InvoiceSignDialog
            :taskInfo="taskInfo"
            ref="invoiceSignDialog"
            @updateTask="v => updateTask(v)"
            :key="'invoiceSignKey' + key"
            @updateData="updateData"
        />
      </v-col>
      <v-col>
        <InvoiceDocumentationDialog
          @closeDialog="updateData"
          :taskInfo="taskInfo"
          ref="invoiceDocumentationDialog"
          :key="'invoiceDialog' + key"
          />
      </v-col>
      <v-col>
        <TwoButtonDialog
            ref="deleteButton"
            :btn-text="$t('buttons:delete')"
            color="red"
            :dialog-title="$t('dialog:delete-btn-title', {name : $t('book/issue:offerDocumentation')})"
            :dialog-text="$t('dialog:delete-btn-dialog', {name : $t('book/issue:offerDocumentation')})"
            :button1-text="$t('buttons:cancel')"
            :button2-text="$t('buttons:delete')"
            @clickedButton1="$refs.deleteButton.showDialog = false"
            @clickedButton2="deleteInvoice"
            hide-button
            hide-overlay
        />
      </v-col>
    </v-row>
  </v-container>
</template>
<script>

import InvoiceDocumentationService from '@/modules/book/services/task-documentation/invoice-documentation-service';
import InvoiceDocumentationDialog from '@/modules/book/views/task/components/InvoiceDocumentationDialog.vue';
import StatusDecorator from '@/modules/book/views/task/dialog-components/invoice-tab/StatusDecorator.vue';
import InvoiceSignDialog from '@/modules/book/views/task/dialog-components/InvoiceSignDialog.vue';
import DataTablePaginated from '@/modules/core/components/DataTable/DataTablePaginated.vue';
import TwoButtonDialog from '@/modules/core/components/TwoButtonDialog/TwoButtonDialog.vue';
import OfferDocumentation from '@/modules/book/data/offer-documentation';
import DataUtils from '@/modules/core/services/data-utils';
import Utils from '@/modules/core/services/utils';
import {i18n} from '@/plugins/i18next';
import Vue from 'vue';

export default {
  name: 'InvoiceTab',
  components: {TwoButtonDialog, InvoiceDocumentationDialog, InvoiceSignDialog, DataTablePaginated},
  props: {
    taskInfo: {}
  },
  data() {
    return {
      key: 1,
      invoiceCount: 0,
      invoiceToDelete: {}
    }
  },
  computed: {
    headers() {
      let header = [
        { text: this.$t('book/issue:common.supplier'), value: 'supplier', default: true, sortable:false },
        { text: this.$t('common:price'), value: 'price', default: true, sortable:false },
        { text: this.$t('common:valid-from'), value: 'startDate', default: true, sortable:false },
        { text: this.$t('common:valid-until'), value: 'endDate', default: true, sortable:false },
        { text: this.$t('common:status'), value: 'status', default: true, sortable: false },
        { text: "", value: "controls", sortable: false, align: 'end' }
      ];

      return header;
    },
    isThereInvoices() {
      return this.invoiceCount > 0;
    }
  },
  watch: {
    taskInfo() {
      this.$refs.table.callTableUpdate();
      this.key++;
    }
  },
  methods: {
    updateTask(task) {
      if (!task) return;
      this.$emit('updateTask', task);
    },
    createInvoice() {
      this.$refs.invoiceDocumentationDialog.openCreationDialog(this.taskInfo);
    },
    deleteInvoice() {
      if (!this.invoiceToDelete || !this.invoiceToDelete.id) return;
      InvoiceDocumentationService.removeInvoice(
          this.invoiceToDelete,
          () => {
            Utils.showSuccessSnackbar(this.$t('snackbar:delete-success', {name: this.$t('book/issue:invoiceDocumentation')}));
            this.$refs.table.callTableUpdate();
            this.$refs.deleteButton.showDialog = false;
            this.updateData();
          },
          () => {
            Utils.showFailureSnackbar(this.$t('snackbar:delete-failure', {name: this.$t('book/issue:invoiceDocumentation')}));
            this.$refs.table.callTableUpdate();
            this.$refs.deleteButton.showDialog = false;
          }
      );
    },
    updateTable(options, search) {
      if (!this.taskInfo || !this.taskInfo.id) {
        this.$refs.table.fillTableData([]);
        return;
      }

      if (!search) search = '';

      InvoiceDocumentationService.readAllPaginatedByTaskIdFromAPI(
          this.taskInfo.id,
          options.page - 1,
          options.itemsPerPage,
          search,
          options.sortBy,
          options.sortDesc,
          (response) => {
            if (this.$refs.table !== undefined) {
              response.data.content = DataUtils.parseDataToObjectArray(response.data.content, OfferDocumentation);
              this.invoiceCount = response.data.content.length;
              for (let i = 0; i < response.data.content.length; i++) {
                response.data.content[i].isSigned = this.isItemSigned(response.data.content[i]);
                response.data.content[i].editable = !response.data.content[i].isSigned;
              }
              this.$refs.table.fillTableData(response.data);
            }
          },
          (error) => {
            this.$refs.table.loading = false;
            Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('book/issue:offerDocumentation')}));
            this.$logger.error(error);
          }
      )
    },
    clearData() {
    },
    isItemSigned(document) {
      let isSigned = false;

      this.taskInfo.taskStatus.forEach(status => {
        if (status.action === 'INVOICE_SIGN' && status.objectId === document.id
            || status.action === 'INVOICE_SEND' && status.objectId === document.id) {
          isSigned = true;
        }
      });

      return isSigned;
    },
    updateData() {
      this.$refs.table.callTableUpdate();
      this.key++;
      this.$emit('updateData')
    },
    openDeleteDialog(invoice) {
      this.$refs.deleteButton.showDialog = true;
      this.invoiceToDelete = invoice;
    },
    decorateStatus(item) {
      let StatusDecoratorComponent = Vue.extend(StatusDecorator);
      item.taskStatus = this.taskInfo.taskStatus;
      let instance = new StatusDecoratorComponent({
        i18n,
        propsData: { item: item }
      });
      return instance;
    }
  }
}
</script>